// THE account implementation (docs/09 §9). One login path, one token store, one
// rotation lock, for all three hosts — iOS (JSC), Android (headless WebView) and
// the web app, which reaches it through the thin `shell/accountService.ts`
// adapter. It absorbed the web's `AuthClient`/`TokenManager` ceremonies; what
// used to be two implementations drifting apart is now this file plus injected
// platform seams.
//
// Everything platform-shaped is a dep, never an import (see BrainAccountDeps):
// the passkey ceremony (ASAuthorization / Credential Manager /
// navigator.credentials), Argon2id (native module / hash-wasm), KV + secret
// cipher (Keychain / Keystore / IndexedDB+WebCrypto), `fetch`, and the session
// kind. That is what keeps this module WASM-free and Capacitor-free — the brain
// bundle must never pull in `identity/webauthn.ts`, whose
// `await import('../shell/capacitor/index.js')` would drag @capacitor/core
// (~800 KB, browser-only) in with it.
//
// Owns: register, challenge/verify key login, password login, passkey-PRF
// identity restore, set/remove password, delete, add-passkey, and rotating
// refresh under the cross-writer single-writer rotation lock
// (storage/rotationLock.ts, ios/BOUNDARY.md §2.10).

import type {
  DeleteMeRequest,
  PasskeyRegisterOptionsResponse,
  PasskeySummary,
  SessionKind,
  TokenBundle,
  WebAuthnJson,
} from '@merkury/protocol';
import {
  RegisterResponse,
  AuthChallengeResponse,
  AuthVerifyResponse,
  PasswordLoginResponse,
  PasskeyLoginOptionsResponse,
  PasskeyLoginResponse,
  RefreshResponse,
  SIG_DOMAIN,
} from '@merkury/protocol';
import { concatBytes, utf8, fromBase64, toBase64, randomBytes } from '../lib/bytes.js';
import {
  keysFromSeed,
  generateIdentity,
  signWithIdentity,
  type IdentityKeys,
} from '../identity/keys.js';
import { seedToPhrase, phraseToSeed } from '../identity/phrase.js';
import {
  deriveSplitKeys,
  decryptIdentitySeed,
  derivePasskeyWrapKey,
  encryptIdentitySeed,
  generateKdfSalt,
  PRF_RESTORE_SALT,
  type Argon2idProvider,
} from '../identity/kdf.js';
import { AccountStore, type StoredAccount } from '../storage/accountStore.js';
import type { KvStore } from '../storage/kv.js';
import type { SecretCipher } from '../storage/secretStore.js';
import { RotationLock, TOKEN_ROTATION_LOCK_KEY } from '../storage/rotationLock.js';
import {
  b64urlToBytes,
  bytesToB64url,
  unwrapPublicKey,
  type PasskeyProvider,
} from '../identity/passkeyProvider.js';
import { defaultFetch, withDeadline } from '../lib/http.js';
import { RestError, routeFailures, staticOrigin, type RestClient, type RestOrigin } from '../api/client.js';

/** Domain-separated auth-message layout (docs/03 §3), identical to
 *  identity/auth.ts `buildAuthMessage` — reconstructed here to avoid importing
 *  that module (which drags WebAuthn/Capacitor). Not a wire type. */
function buildAuthMessage(nonce: Uint8Array, userId: string): Uint8Array {
  return concatBytes(utf8(SIG_DOMAIN.AUTH), nonce, utf8(userId));
}

/** Account-deletion message (docs/03 §3b), identical to identity/auth.ts
 *  `buildDeleteMessage` — note the operand order differs from the auth message:
 *  prefix ‖ userId ‖ nonce, matching the server's `auth.DeleteMessage`. */
function buildDeleteMessage(userId: string, nonce: Uint8Array): Uint8Array {
  return concatBytes(utf8(SIG_DOMAIN.DELETE), utf8(userId), nonce);
}

/** The account password was demanded on a key login (2FA, docs/03 §3a) and no
 *  correct proof was supplied — the caller must prompt then retry by password. */
export class PasswordRequiredError extends Error {
  constructor(readonly userId: string) {
    super('password required');
    this.name = 'PasswordRequiredError';
  }
}

/** REST error carrying the HTTP status + protocol code (subset of RestError). */
export class AuthHttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'AuthHttpError';
  }
}

/**
 * A passkey-restore ceremony ran but could not reconstruct the identity — no PRF
 * output, no server-returned ciphertext, or the blob decrypts under neither PRF
 * key. `code` reuses the native passkeys seam vocabulary (`unsupported` here) so
 * the main.ts handler maps seam errors and this one through one branch. The
 * seam's own `cancelled`/`no-credential`/`error` propagate on their own errors'
 * `.code`.
 */
export class PasskeyRestoreError extends Error {
  constructor(readonly code: string, message?: string) {
    super(message ?? code);
    this.name = 'PasskeyRestoreError';
  }
}

/** commitCreate was called with no seed held from a prior prepareCreate — the
 *  two-phase create was never begun. main.ts maps this to the BAD_ARGS bridge
 *  code (iW10-A). */
export class NoPendingCreateError extends Error {
  constructor() {
    super('no prepared create seed');
    this.name = 'NoPendingCreateError';
  }
}

/**
 * Provisioning persisted the identity locally but could not reach the server to
 * register it (offline, DNS poisoned, origin blocked). Raised ONLY by hosts that
 * opted into offline provisioning (`offlineProvision`), where onboarding must
 * finish anyway: the device holds the identity, which is the part that cannot be
 * recreated, and `load()` retries the registration on the next launch.
 *
 * It carries the account exactly as persisted so the caller can land the user in
 * the app rather than stranding them on a spinner because the network happened
 * to be hostile at that moment (prime directive). `cause` is the underlying
 * transport/HTTP failure, for logging — never for a user-facing message.
 */
export class RegistrationPendingError extends Error {
  constructor(
    readonly account: StoredAccount,
    readonly identity: IdentityKeys,
    override readonly cause: unknown,
  ) {
    super('registration pending');
    this.name = 'RegistrationPendingError';
  }
}

/**
 * Neutral display-name placeholder for an account committed without a name yet
 * (two-phase create — the name/avatar step follows), and the shared restore
 * fallback. Server validation accepts any string up to 64 runes, even ""
 * (backend httpapi validate.go `parseRegister`: `utf8.RuneCountInString(name)
 * > 64` is the only bound; protocol primitives.ts `DisplayName =
 * z.string().max(64)`). Created-unnamed accounts get a LocalSend-style
 * generated name ("Cool Mango") from the SHARED cross-platform generator —
 * ./placeholderName.ts (dependency-free, brain-safe).
 */
import { friendlyPlaceholderName } from './placeholderName.js';

/** Restore paths only: the server keeps the account's existing name (D17/D34),
 *  so this value is never user-visible there — any non-empty string works. */
const DEFAULT_DISPLAY_NAME = 'Merkury user';

/** A 401/403 from any of our HTTP error shapes (AuthHttpError here, RestError
 *  from the REST client, the app's ApiError). Recognized by SHAPE, not
 *  `instanceof`: the same failure arrives as a different class depending on
 *  which client issued the call. */
function authRejection(err: unknown): { status: number; code?: unknown } | undefined {
  const e = err as { status?: unknown; code?: unknown } | null;
  if (!e || typeof e.status !== 'number') return undefined;
  if (e.status !== 401 && e.status !== 403) return undefined;
  return { status: e.status, code: e.code };
}

/**
 * Whether a refresh failure really means "this session is over", as opposed to
 * something BETWEEN us and the server saying no.
 *
 * The server sets a protocol `code` on every error it emits (httpapi
 * respond.go `writeError` is the only path), so a 401/403 carrying no code did
 * not come from the server: it is a Cloudflare challenge page, a captive
 * portal, or — in this app's actual threat model — an interceptor. Treating
 * those as authoritative made the client discard its resume state and mint a
 * brand-new session on every intercepted refresh, filling the user's session
 * list with phantom devices. When in doubt, keep the session: a refresh can
 * always be retried, a discarded one cannot be taken back.
 */
function isSessionDeath(err: unknown): boolean {
  const rejection = authRejection(err);
  return rejection !== undefined && rejection.code !== undefined;
}

/** How long a prefetched ceremony challenge stays usable. The server's own TTL
 *  is short; a stale one would fail the assertion, so we refetch instead. */
const PRIMED_OPTIONS_TTL_MS = 90_000;

/** Re-exported for the hosts that pin the slot name (Swift's
 *  `ColdTokenRefresher.rotationLockKey`); the lock itself lives in
 *  storage/rotationLock.ts, shared with the web service worker. */
export { TOKEN_ROTATION_LOCK_KEY };

export interface BrainAccountDeps {
  kv: KvStore;
  /** Platform secret wrapping (Keychain / Keystore). Omitted ⇒ the default
   *  non-extractable WebCrypto AES-GCM key kept in the same KV (storage/
   *  secretStore.ts), which is what web and iOS already use. */
  secretCipher?: SecretCipher;
  /**
   * REST base for the auth endpoints — a fixed string, or the brain's
   * `RestOrigin`, which re-resolves per request so login and refresh follow the
   * endpoint the signaling race won instead of a build-time host a censor can
   * freeze (docs/04 §2).
   */
  baseUrl: string | RestOrigin;
  argon2: Argon2idProvider;
  /** The WebAuthn ceremony seam (identity/passkeyProvider.ts). */
  passkeys: PasskeyProvider;
  sessionKind?: SessionKind;
  /** Persist the boot-config userId so a no-arg cold reboot finds the account.
   *  A no-op on hosts that keep the userId only inside the stored account. */
  persistUserId: (userId: string) => void;
  /**
   * HTTP for the unauthenticated auth endpoints. Defaults to `defaultFetch()`
   * — bounded per attempt and retrying — because login and refresh are the
   * first calls a cold app makes and an unbounded one strands the whole
   * session behind a stalled socket (lib/http.ts).
   */
  fetchFn?: typeof fetch;
  /**
   * Account-scoped state that lives OUTSIDE the kv/SecretStore wipe — on the
   * web the localStorage contacts cache, favorites, aliases and call log.
   * Fired by sign-out and by account deletion so the previous account's data
   * never survives into the next one.
   */
  clearLocalData?: () => void | Promise<void>;
  /**
   * Let onboarding finish when the server is unreachable: `provision()` rejects
   * with `RegistrationPendingError` (carrying the locally persisted account)
   * instead of a transport error, and `load()` retries the registration on the
   * next launch. The web/PWA sets it — a browser tab that cannot reach the API
   * for one request must still end up with a usable, browsable app (prime
   * directive). iOS/Android leave it off: their onboarding retries the commit
   * explicitly and wants the failure surfaced then and there.
   */
  offlineProvision?: boolean;
  now?: () => number;
  /** Refresh this many ms before nominal access-token expiry. */
  skewMs?: number;
  /**
   * Ceiling on a single `getAccessToken()` call. Undefined ⇒ unbounded, which
   * only tests should want: `RestClient.request` awaits the token before it can
   * send anything, and the recovery chain — refresh, refresh again,
   * challenge+verify, passkey login — is several serial round trips. Unbounded,
   * it outlived the native bridge's 30 s budget and every screen reported "not
   * connected yet" without one request having been issued, and on a censored
   * link (where new TLS flows freeze rather than fail) it never returned at all.
   * The runtime wires `TOKEN_ACQUISITION_BUDGET_MS` (lib/http.ts) for every host.
   */
  tokenBudgetMs?: number;
  /**
   * Bound on waiting for the rotation lock. TESTS ONLY — production wires
   * nothing and takes `LOCK_WAIT_CAP_MS`.
   *
   * Shortening it is not a latency optimisation, it is a correctness change:
   * `RotationLock.acquire()` does not reject on the cap, it PROCEEDS without
   * the lock (storage/rotationLock.ts — advisory by design), and the holder it
   * gave up on may still be mid-POST, since a non-replayable auth POST is
   * allowed `HTTP_LAST_ATTEMPT_TIMEOUT_MS`. Two writers then present the same
   * refresh token, which is the one failure the lock exists to prevent. A
   * caller's token budget expiring is cheap by comparison — `withDeadline`
   * does not cancel the work, so the refresh still completes for the next
   * caller.
   */
  lockWaitMs?: number;
}

/** Result of account create/restore handed back over the rpc boundary. */
export interface AccountReady {
  userId: string;
  account: StoredAccount;
  identity: IdentityKeys;
}

/** What `load()` finds on disk. `userId` is absent only for an account whose
 *  registration never completed — the identity is there, the server handshake
 *  is not (see BrainAccount.load). */
export interface LoadedAccount {
  userId?: string;
  account: StoredAccount;
  identity: IdentityKeys;
}

/**
 * The brain's account brain-stem: identity persistence, server registration,
 * login, and a single-flight + cross-process-locked token provider. Holds NO
 * engine — main.ts brings the engine up once this reports an account ready.
 */
export class BrainAccount {
  private readonly deps: BrainAccountDeps;
  private store: AccountStore | undefined;
  private readonly lock: RotationLock;
  private readonly fetchFn: typeof fetch;
  private readonly origin: RestOrigin;

  /** Set once an identity is present so a mid-session 401 can silently relogin. */
  private credentials: { userId: string; identity: IdentityKeys } | undefined;

  /** Two-phase create (iW10-A): a freshly generated identity + its 24-word phrase
   *  held IN MEMORY ONLY between `prepareCreate` and `commitCreate`. Never
   *  persisted, never sent — an abandoned prepare leaves zero trace. */
  private pendingCreate: { identity: IdentityKeys; words: string[] } | undefined;

  /** Prefetched passkey ceremony options, warmed BEFORE the user gesture (see
   *  primePasskeyRestore / primeAddPasskey). */
  private primedRestore: { options: PasskeyLoginOptionsResponse; at: number } | undefined;
  private primedRegister: { options: PasskeyRegisterOptionsResponse; at: number } | undefined;

  private tokenState:
    | { accessToken: string; refreshToken: string; expiresAt: number; sessionId: string }
    | undefined;
  private refreshInflight: Promise<TokenBundle> | undefined;
  private loginInflight: Promise<void> | undefined;

  /**
   * A refresh token whose last POST died in TRANSPORT — no verdict, so the
   * server may or may not have rotated it — plus the failure to re-raise.
   * Armed and consulted in `refresh()`, which is the ONLY method that presents
   * a refresh token, so every path is covered: the single-flight refresh, its
   * recovery leg, and `login()`'s resume leg.
   *
   * This matters more now that REST rotates origins: the first POST could
   * freeze against a censored host while the second lands on a live mirror, so
   * "it never reached the server anyway" stopped being true. The server heals a
   * lost rotation response only while nobody has used the successor
   * (session/store.go Refresh) — with a second writer in the picture a blind
   * re-presentation is what turns a lost response into ErrTokenReuse and takes
   * the whole family down.
   *
   * Dropped when the acquisition chain that would have replayed it settles
   * (`acquireAccessToken`), never inside it: the caller has been told, and its
   * NEXT attempt — a reconnect backoff, a user retry — may present the token
   * once more. So one presentation per attempt, never two in a row.
   *
   * The price, paid knowingly: a transient blip during a refresh now surfaces
   * as one failed acquisition where the immediate second leg used to paper over
   * it, and a `forceFresh` acquisition after a WS 4001/4403 costs one reconnect
   * backoff cycle. Both self-heal on the very next attempt. A duplicate
   * rotation does not: with a second writer in the picture it takes the session
   * family down, and no retry gets that back.
   */
  private refreshInDoubt: { token: string; err: unknown } | undefined;

  /** Nesting depth of `getAccessToken` chains in flight; the doubt above is
   *  dropped when the last one settles, not when the first leg reads it. */
  private acquiring = 0;

  constructor(deps: BrainAccountDeps) {
    this.deps = deps;
    this.origin = typeof deps.baseUrl === 'string' ? staticOrigin(deps.baseUrl) : deps.baseUrl;
    // Rotation-aware by construction (api/client.ts): a transport failure moves
    // the NEXT auth call to another origin. It never replays the failed one —
    // `POST /auth/refresh` consumes its refresh token, so a duplicate would end
    // the session family.
    this.fetchFn = routeFailures(this.origin, deps.fetchFn ?? defaultFetch());
    this.lock = new RotationLock(deps.kv, {
      ...(deps.now ? { now: deps.now } : {}),
      ...(deps.lockWaitMs !== undefined ? { waitCapMs: deps.lockWaitMs } : {}),
    });
  }

  private async openStore(): Promise<AccountStore> {
    this.store ??= await AccountStore.open(this.deps.kv, this.deps.secretCipher);
    return this.store;
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now();
  }

  // --- launch: restore an existing account from device storage ---------------

  /**
   * Load an already-provisioned account off the secret/kv seams. Returns
   * undefined only when onboarding is genuinely required (no identity, no
   * account). Kicks off a background login so a token is warming by the time the
   * engine needs one; the login rejection is swallowed (offline-tolerant, prime
   * directive).
   *
   * `userId` is absent when a previous onboarding persisted the identity but
   * never got a registration through (see RegistrationPendingError): the
   * account still loads — the device holds the identity, so this is a signed-in
   * user with an unfinished handshake, not a fresh install — and registration
   * is retried in the background. A host that needs a userId before it can do
   * anything (the iOS engine bring-up) simply treats the absent one as
   * onboarding-required.
   */
  async load(): Promise<LoadedAccount | undefined> {
    const store = await this.openStore();
    const [account, identity] = await Promise.all([store.loadAccount(), store.loadIdentity()]);
    if (!account || !identity) return undefined;
    if (account.userId === undefined) {
      void this.retryRegistration(account, identity).catch(() => undefined);
      return { account, identity };
    }
    this.credentials = { userId: account.userId, identity };
    void this.ensureLogin(account.userId, identity).catch(() => undefined);
    return { userId: account.userId, account, identity };
  }

  /** Finish an onboarding whose `POST /register` never landed (offline). */
  private async retryRegistration(
    account: StoredAccount,
    identity: IdentityKeys,
  ): Promise<void> {
    const { userId } = await this.register(identity.publicKeyB64, account.displayName);
    const store = await this.openStore();
    await store.saveAccount({ ...account, userId });
    this.deps.persistUserId(userId);
    this.credentials = { userId, identity };
    await this.login(userId, identity);
  }

  // --- account creation ------------------------------------------------------

  /**
   * Two-phase create, phase 1 (iW10-A): generate a fresh identity + recovery
   * phrase and HOLD them in memory only, so the iOS onboarding can show the
   * phrase (→ quiz) BEFORE anything is registered. Touches neither the server
   * nor persistent storage — an abandoned prepare leaves zero trace, and the
   * account stays onboarding-required. Calling again discards the previous
   * pending seed and returns fresh words (back-navigation = regenerate).
   */
  prepareCreate(): { words: string[] } {
    const identity = generateIdentity();
    const words = seedToPhrase(identity.seed).split(' ');
    this.pendingCreate = { identity, words };
    return { words };
  }

  /**
   * Drop the seed a `prepareCreate` is holding — backing out of onboarding must
   * leave zero trace. The seed lives in memory only (never the server, never
   * storage), so clearing the field IS the discard. Idempotent, and deliberately
   * touches nothing else: a signed-in caller must not be logged out by it.
   */
  discardPendingCreate(): void {
    this.pendingCreate = undefined;
  }

  /**
   * Two-phase create, phase 2 (iW10-A): register the HELD seed (the exact same
   * register / challenge-verify / persist path as the atomic `create`, shared
   * via `provision` — no duplicated crypto) and land ready. `displayName`
   * optional: absent → DEFAULT_DISPLAY_NAME. Throws `NoPendingCreateError`
   * (→ BAD_ARGS) when no seed was prepared. On any failure (server down, kind
   * rejected) the pending seed STAYS held, so the UI can retry commit without
   * re-showing new words; it is cleared only on success.
   */
  async commitCreate(
    displayName?: string,
    avatarSeed?: number,
  ): Promise<AccountReady & { words: string[] }> {
    const pending = this.pendingCreate;
    if (!pending) throw new NoPendingCreateError();
    const ready = await this.provision(
      pending.identity,
      displayName || friendlyPlaceholderName(),
      false,
      avatarSeed,
    );
    this.pendingCreate = undefined;
    return { ...ready, words: pending.words };
  }

  /** Fresh account (atomic): prepare + commit in one call — generate identity,
   *  persist, register, log in. Returns the account plus the 24-word recovery
   *  phrase the UI must show once. */
  async create(displayName: string, avatarSeed?: number): Promise<AccountReady & { words: string[] }> {
    this.prepareCreate();
    return this.commitCreate(displayName || undefined, avatarSeed);
  }

  /** Restore by 24-word recovery phrase: re-derive the identity, register
   *  (idempotent for a known publicKey → same userId), log in. When the account
   *  has a password set, login throws PasswordRequiredError and the caller must
   *  fall back to restorePassword or retry with the typed password. */
  async restorePhrase(
    phrase: string,
    displayName: string | undefined,
    password: string | undefined,
  ): Promise<AccountReady> {
    let identity: IdentityKeys;
    try {
      identity = keysFromSeed(phraseToSeed(phrase));
    } catch {
      throw new AuthHttpError(400, undefined, 'INVALID_PHRASE');
    }
    return this.provision(identity, displayName || DEFAULT_DISPLAY_NAME, true, undefined, password);
  }

  /** Restore by account password (docs/03 §3a): the server hands back the
   *  identity seed encrypted under the password's independent enc branch; we
   *  decrypt it locally, persist, and land ready — no phrase needed. */
  async restorePassword(userId: string, password: string): Promise<AccountReady> {
    const { tokens, seed } = await this.loginWithPassword(userId, password);
    const identity = keysFromSeed(seed);
    const store = await this.openStore();
    await store.saveIdentity(identity);
    const account: StoredAccount = {
      displayName: DEFAULT_DISPLAY_NAME,
      publicKeyB64: identity.publicKeyB64,
      restored: true,
      createdAt: new Date().toISOString(),
      userId,
    };
    await store.saveAccount(account);
    this.deps.persistUserId(userId);
    this.credentials = { userId, identity };
    this.setTokens(tokens);
    await store.saveRefreshToken(tokens.refreshToken);
    // Persist the password proof so a later silent relogin attaches it (2FA).
    const authKey = await this.deriveAuthKey(userId, password);
    await store.saveAuthKey(authKey);
    return { userId, account, identity };
  }

  /**
   * True when this host can run passkey ceremonies at all. UI gates the passkey
   * affordances on it so the paths never appear where they cannot work.
   */
  passkeysSupported(): boolean {
    return this.deps.passkeys.supported();
  }

  private fetchRestoreOptions(userId?: string): Promise<PasskeyLoginOptionsResponse> {
    return this.postUnauth('/auth/passkeys/login/options', userId ? { userId } : {}).then((r) =>
      PasskeyLoginOptionsResponse.parse(r),
    );
  }

  /**
   * Warm the passkey-restore options ahead of the tap so `restorePasskey` runs
   * the ceremony with NO preceding network await. This is not a nicety: Safari
   * only honours `navigator.credentials.get` inside the transient user
   * activation of the click, and a round trip in between loses it — the
   * ceremony then rejects with NotAllowedError and looks like a cancellation.
   * The native seams have the same shape of constraint. Only the DISCOVERABLE
   * one-tap flow can be primed (the legacy by-ID flow's account id isn't known
   * until the user types it). Best-effort: any failure just leaves the cache
   * empty and restore fetches inline.
   */
  async primePasskeyRestore(): Promise<void> {
    try {
      const options = await this.fetchRestoreOptions();
      this.primedRestore = { options, at: this.now() };
    } catch {
      // best-effort; restorePasskey will fetch inline
    }
  }

  /** Take a prefetch if it is fresh and matches this (discoverable) flow. The
   *  server challenge is single-use, so it is always cleared on read. */
  private takePrimedRestore(userId?: string): PasskeyLoginOptionsResponse | undefined {
    const primed = this.primedRestore;
    this.primedRestore = undefined;
    if (userId || !primed) return undefined;
    // Discard a stale challenge (the server TTL is short); fetch fresh instead.
    return this.now() - primed.at < PRIMED_OPTIONS_TTL_MS ? primed.options : undefined;
  }

  /**
   * Passkey-PRF identity restore on a new device (docs/03 §2). Discoverable
   * (usernameless) by default; a `userId` targets one account's credentials and
   * ALSO evaluates its legacy per-user PRF salt so old wrapped blobs still
   * restore. Flow: fetch assertion options → run the ceremony through the
   * injected passkey provider with both PRF salts → exchange the assertion for
   * tokens + the wrapped-seed ciphertext → HKDF the PRF output into the wrap key
   * → decrypt the seed → land the account exactly like `restorePassword`.
   *
   * Unlike the pre-merge web path this KEEPS the session the passkey login just
   * minted rather than discarding it and re-registering (docs/09 §9 ruling): the
   * server already authenticated this device, so a second round trip buys
   * nothing (prime directive).
   *
   * Throws `PasskeyRestoreError` for every ran-but-nothing-restorable outcome —
   * `no-credential` when the ceremony produced nothing, `unsupported` when there
   * is no PRF output, no ciphertext, the blob decrypts under neither key, or no
   * user handle names the account. The provider's own `cancelled`/`wrong-origin`
   * errors propagate unchanged (their code rides `.code`). Never logs the seed,
   * tokens, or PRF output.
   */
  async restorePasskey(userId?: string): Promise<AccountReady> {
    // Consume a prefetch when one is warm so no server round-trip sits between
    // the user gesture and the ceremony (Safari transient activation) — a miss
    // just falls back to fetching inline.
    const options = this.takePrimedRestore(userId) ?? (await this.fetchRestoreOptions(userId));
    // `first` = the fixed app-wide restore salt, `second` = the account's legacy
    // per-user salt when the server supplied one; both are evaluated in ONE
    // assertion so old blobs still restore.
    const second = options.prfSalt ? fromBase64(options.prfSalt) : undefined;
    const ceremony = await this.deps.passkeys.assert({
      options: options.publicKey,
      prf: { first: PRF_RESTORE_SALT, ...(second ? { second } : {}) },
    });
    if (!ceremony) throw new PasskeyRestoreError('no-credential', 'ceremony yielded no credential');
    if (!ceremony.prfOutput) {
      throw new PasskeyRestoreError('unsupported', 'ceremony yielded no PRF output');
    }
    const credential = ceremony.credential as Record<string, unknown>;

    const response = PasskeyLoginResponse.parse(
      await this.postUnauth('/auth/passkeys/login', {
        ...(userId ? { userId } : {}),
        // Strip the PRF outputs from the client-extension results before they
        // leave the device — the server NEVER sees the PRF secret (docs/03 §2,
        // identity/kdf.ts). The pre-merge web path only got away without this
        // because browser PRF results are ArrayBuffers that JSON-serialize to
        // `{}`; the Capacitor plugin returns base64url STRINGS, so that path was
        // posting real PRF secrets to the server until this line existed.
        credential: withoutPrfResults(credential),
        kind: this.deps.sessionKind ?? 'web',
      }),
    );
    if (!response.encIdentityKey) throw new PasskeyRestoreError('unsupported', 'no seed ciphertext');
    const seed = unwrapPasskeySeed(
      fromBase64(response.encIdentityKey),
      ceremony.prfOutput,
      ceremony.prfOutputSecond,
    );
    if (!seed) throw new PasskeyRestoreError('unsupported', 'seed decrypts under no PRF key');

    // Discoverable flow: the server sets the credential's userHandle to the
    // account UUID (backend passkeys.go `WebAuthnID()`), so the assertion names
    // the account with nothing typed. Targeted flow: use the supplied id.
    const resolvedUserId = userId ?? userHandleUserId(credential);
    if (!resolvedUserId) throw new PasskeyRestoreError('unsupported', 'assertion carried no user handle');

    const identity = keysFromSeed(seed);
    const store = await this.openStore();
    await store.saveIdentity(identity);
    const account: StoredAccount = {
      displayName: DEFAULT_DISPLAY_NAME,
      publicKeyB64: identity.publicKeyB64,
      restored: true,
      createdAt: new Date().toISOString(),
      userId: resolvedUserId,
    };
    await store.saveAccount(account);
    this.deps.persistUserId(resolvedUserId);
    this.credentials = { userId: resolvedUserId, identity };
    const { encIdentityKey: _drop, ...tokens } = response;
    this.setTokens(tokens);
    await store.saveRefreshToken(tokens.refreshToken);
    return { userId: resolvedUserId, account, identity };
  }

  /** Persist server-reconciled profile fields into the stored account. The
   *  restore paths land with the placeholder name (the server keeps the real
   *  one on idempotent register), so the engine reconciles from `GET /me` at
   *  bring-up and persists here — a cold launch then shows the right name even
   *  offline. No-op while signed out. */
  async persistProfile(patch: { displayName?: string; avatarSeed?: number }): Promise<void> {
    const store = await this.openStore();
    const account = await store.loadAccount();
    if (!account) return;
    await store.saveAccount({ ...account, ...patch });
  }

  /** Persist / clear the password-derived authKey (the 2FA proof) so a silent
   *  re-login attaches the current one and never re-prompts (docs/03 §3a).
   *  `setPassword`/`removePassword` do this themselves; this is the seam for
   *  hosts still driving those ceremonies outside the brain (the Capacitor
   *  bridge, until B3-android). */
  async setStoredAuthKey(authKey: Uint8Array | undefined): Promise<void> {
    const store = await this.openStore();
    if (authKey) await store.saveAuthKey(authKey);
    else await store.deleteAuthKey();
  }

  /** Persist the chosen avatar seed, returning the updated account so a caller
   *  mirroring it into UI state can re-render (undefined when nothing is stored
   *  yet). A no-op — same account back — when the seed is unchanged, so a
   *  `GET /me` reconciliation that agrees with local state never churns. */
  async setAvatarSeed(seed: number): Promise<StoredAccount | undefined> {
    const store = await this.openStore();
    return store.setAvatarSeed(seed);
  }

  /**
   * Provision an identity the CALLER already holds. The web onboarding flow
   * generates the seed (or derives it from a typed phrase) in React and hands
   * the finished keys over, so it needs the register/persist/login tail without
   * the brain's own generation step — this is that tail, and it is the exact
   * same `provision()` the two-phase create uses. No duplicated crypto.
   */
  adoptIdentity(
    identity: IdentityKeys,
    opts: {
      displayName?: string;
      restored: boolean;
      avatarSeed?: number;
      /** The account password, when the server demanded it (2FA restore). */
      password?: string;
    },
  ): Promise<AccountReady> {
    return this.provision(
      identity,
      opts.displayName || (opts.restored ? DEFAULT_DISPLAY_NAME : friendlyPlaceholderName()),
      opts.restored,
      opts.avatarSeed,
      opts.password,
    );
  }

  /** Shared create/restore-by-phrase tail: persist identity + a provisional
   *  account, register (idempotent), then first login. */
  private async provision(
    identity: IdentityKeys,
    displayName: string,
    restored: boolean,
    avatarSeed?: number,
    restorePassword?: string,
  ): Promise<AccountReady> {
    const store = await this.openStore();
    const provisional: StoredAccount = {
      displayName,
      publicKeyB64: identity.publicKeyB64,
      restored,
      createdAt: new Date().toISOString(),
      ...(avatarSeed !== undefined ? { avatarSeed } : {}),
    };
    // Persist identity + account FIRST — local state is the truth; a failed
    // registration retries next launch (prime directive).
    await store.saveIdentity(identity);
    await store.saveAccount(provisional);

    let userId: string;
    try {
      ({ userId } = await this.register(identity.publicKeyB64, displayName));
    } catch (err) {
      // The identity is safe on disk. On a host that opted into offline
      // provisioning, finishing onboarding beats stranding the user because the
      // network was hostile for one request — but a 2FA restore is exempt: the
      // user is ACTIVELY submitting a password and every failure there must be
      // visible, or a rate limit silently "completes" onboarding with no session
      // and parks them on a yellow dot.
      if (this.deps.offlineProvision && !restorePassword) {
        throw new RegistrationPendingError(provisional, identity, err);
      }
      throw err;
    }
    const account: StoredAccount = { ...provisional, userId };
    await store.saveAccount(account);
    this.deps.persistUserId(userId);
    this.credentials = { userId, identity };

    try {
      await this.firstLogin(userId, identity, restorePassword);
    } catch (err) {
      // Registered but not logged in: same reasoning as above, and `load()` will
      // find a userId and just log in next launch. PasswordRequiredError always
      // propagates — onboarding has a prompt to show for it.
      if (
        this.deps.offlineProvision &&
        !restorePassword &&
        !(err instanceof PasswordRequiredError)
      ) {
        throw new RegistrationPendingError(account, identity, err);
      }
      throw err;
    }
    return { userId, account, identity };
  }

  /** First login for a just-provisioned account. With a password (2FA restore):
   *  derive the authKey, log in with it, persist it. Otherwise ordinary key
   *  login, which surfaces PasswordRequiredError when the account has one. */
  private async firstLogin(
    userId: string,
    identity: IdentityKeys,
    password?: string,
  ): Promise<void> {
    const store = await this.openStore();
    if (password !== undefined && password !== '') {
      const authKey = await this.deriveAuthKey(userId, password);
      const bundle = await this.loginWithKey(userId, identity.seed, authKey);
      this.setTokens(bundle);
      await store.saveRefreshToken(bundle.refreshToken);
      await store.saveAuthKey(authKey);
      return;
    }
    await this.login(userId, identity);
  }

  // --- token provider (the getToken the engine + REST client share) ----------

  /**
   * Returns a currently-valid access token, refreshing (single-flight, under the
   * rotation lock) when stale, and re-minting a session with the identity key
   * rather than ever giving up while the device still holds the identity
   * (docs/03 §3, prime directive: no session state may be a dead end).
   *
   * `forceFresh` skips the cached token entirely. The signaling client sets it
   * after the SERVER refused the session (WS 4001/4403): the cached token is
   * known-dead there, so handing it back would just fail again and the socket
   * would stay down until the token happened to expire — minutes of being
   * uncallable on a perfectly good network.
   *
   * Bounded as a whole by `tokenBudgetMs` when the host sets one: every leg
   * below is its own round trip, and on a slow link the chain alone used to
   * outlive the native bridge's call budget, so the UI blamed the WebView
   * ("not connected yet") before a single request had been sent. The work keeps
   * running past the deadline on purpose — it may still land a good session for
   * the next caller.
   */
  getAccessToken = (opts?: { forceFresh?: boolean }): Promise<string> => {
    const budget = this.deps.tokenBudgetMs;
    const work = this.acquireAccessToken(opts);
    return budget === undefined ? work : withDeadline(work, budget, 'token acquisition');
  };

  /** One acquisition attempt, start to finish. The `refreshInDoubt` marker must
   *  outlive every leg of it (refresh → recover → relogin) or the leg after a
   *  transport-failed refresh presents the same token again — and now to a
   *  DIFFERENT origin, which is a real second rotation rather than the harmless
   *  retry against the same dead host it used to be. */
  private acquireAccessToken = (opts?: { forceFresh?: boolean }): Promise<string> => {
    this.acquiring += 1;
    return this.acquireOnce(opts).finally(() => {
      this.acquiring -= 1;
      if (this.acquiring === 0) this.refreshInDoubt = undefined;
    });
  };

  private acquireOnce = async (opts?: { forceFresh?: boolean }): Promise<string> => {
    try {
      return await this.tokenOnce(opts);
    } catch (err) {
      // Last resort: the in-memory session is gone or the server rejected the
      // refresh outright. One single-flight re-login from the stored identity,
      // then try once more — this is the leg that rescues a device whose token
      // family was rotated out from under it (another tab, the service worker,
      // the native reach listener).
      const creds = this.credentials;
      if (!creds) throw err;
      await this.ensureLogin(creds.userId, creds.identity);
      return this.tokenOnce();
    }
  };

  private tokenOnce = async (opts?: { forceFresh?: boolean }): Promise<string> => {
    if (opts?.forceFresh) {
      this.tokenState = undefined;
      this.refreshInflight = undefined;
      const creds = this.credentials;
      if (!creds) throw new Error('not authenticated');
      // login() prefers the stored refresh token and falls back to a full key
      // login when the server says that session is dead.
      await this.ensureLogin(creds.userId, creds.identity);
      const fresh = this.currentAccessToken();
      if (fresh === undefined) throw new Error('login produced no session');
      return fresh;
    }
    if (this.tokenState) {
      const skew = this.deps.skewMs ?? 60_000;
      if (this.now() < this.tokenState.expiresAt - skew) return this.tokenState.accessToken;
      const inflight = (this.refreshInflight ??= this.runRefresh());
      try {
        const bundle = await inflight;
        return bundle.accessToken;
      } finally {
        this.refreshInflight = undefined;
      }
    }
    // No session yet (cold, pre-login): relogin from stored identity if we can.
    const creds = this.credentials;
    if (!creds) throw new Error('not authenticated');
    await this.ensureLogin(creds.userId, creds.identity);
    const token = this.currentAccessToken();
    if (token === undefined) throw new Error('login produced no session');
    return token;
  };

  /** Read the current access token (method call re-reads the field with its
   *  declared type, past the caller's control-flow narrowing). */
  private currentAccessToken(): string | undefined {
    return this.tokenState?.accessToken;
  }

  private setTokens(bundle: TokenBundle): void {
    this.refreshInDoubt = undefined; // a landed rotation settles every doubt
    this.tokenState = {
      accessToken: bundle.accessToken,
      refreshToken: bundle.refreshToken,
      expiresAt: this.now() + bundle.expiresIn * 1000,
      sessionId: bundle.sessionId,
    };
  }

  /** The whole refresh — lock, network, rotation-persist, recovery — in one
   *  single-flight promise so side effects fire exactly once for all waiters. */
  private async runRefresh(): Promise<TokenBundle> {
    const store = await this.openStore();
    await this.lock.acquire();
    try {
      // Re-read from storage inside the lock: another writer (the Swift cold
      // client) may have rotated the token while we waited — always spend the
      // freshest one.
      const stored = (await store.loadRefreshToken().catch(() => undefined)) ??
        this.tokenState?.refreshToken;
      if (!stored) throw new Error('no refresh token');
      try {
        const bundle = await this.refresh(stored);
        this.setTokens(bundle);
        await store.saveRefreshToken(bundle.refreshToken);
        return bundle;
      } catch (err) {
        // Always worth a rescue on a 401/403, coded or not: the usual cause is
        // another writer (a second tab, the service worker, the native reach
        // listener) having rotated the token in storage under us.
        // `refresh()` has already marked the token in doubt if no verdict came
        // back, so no leg below re-presents it.
        if (authRejection(err)) {
          const rescued = await this.recover(stored);
          if (rescued) return rescued;
        }
        throw err;
      }
    } finally {
      await this.lock.release();
    }
  }

  /** Last-chance recovery on a rejected refresh: (1) a token another writer
   *  rotated in storage under us, (2) a fresh key login. Only both failing
   *  loses the session. */
  private async recover(failedToken: string): Promise<TokenBundle | undefined> {
    const store = await this.openStore();
    const stored = await store.loadRefreshToken().catch(() => undefined);
    if (stored && stored !== failedToken) {
      try {
        const bundle = await this.refresh(stored);
        this.setTokens(bundle);
        await store.saveRefreshToken(bundle.refreshToken);
        return bundle;
      } catch {
        // fall through to key login
      }
    }
    const creds = this.credentials;
    if (!creds) return undefined;
    const authKey = await store.loadAuthKey().catch(() => undefined);
    try {
      const bundle = await this.loginWithKey(creds.userId, creds.identity.seed, authKey);
      this.setTokens(bundle);
      await store.saveRefreshToken(bundle.refreshToken);
      return bundle;
    } catch {
      return undefined;
    }
  }

  /** Single-flight around login(): a cold start fans out many authenticated
   *  calls at once (contacts, profile, aliases, endpoint refresh), and each
   *  would otherwise race its own challenge/verify for the same user — the
   *  launch-time relogin included. One login serves all concurrent waiters. */
  private ensureLogin(userId: string, identity: IdentityKeys): Promise<void> {
    return (this.loginInflight ??= this.login(userId, identity).finally(() => {
      this.loginInflight = undefined;
    }));
  }

  /** Resume via the stored refresh token when possible (a challenge/verify login
   *  mints a NEW server session — doing it every launch piles up phantom
   *  sessions). Full key login only when there is no stored token or the server
   *  says the session is dead. */
  private async login(userId: string, identity: IdentityKeys): Promise<void> {
    const store = await this.openStore();
    const stored = await store.loadRefreshToken().catch(() => undefined);
    // Cheap pre-check so a token already in doubt does not even take the
    // cross-writer rotation lock; `refresh()` is the authoritative guard. The
    // transport failure is re-raised verbatim rather than minting a session: a
    // fault the server never authored must not cost the user a phantom device
    // (see isSessionDeath), and the caller retries later.
    const doubt = this.refreshInDoubt;
    if (stored !== undefined && stored === doubt?.token) throw doubt.err;
    if (stored) {
      try {
        await this.lock.acquire();
        try {
          const freshest = (await store.loadRefreshToken().catch(() => undefined)) ?? stored;
          const bundle = await this.refresh(freshest);
          this.setTokens(bundle);
          await store.saveRefreshToken(bundle.refreshToken);
          return;
        } finally {
          await this.lock.release();
        }
      } catch (err) {
        // Only a refusal the SERVER actually authored justifies abandoning the
        // resume and minting a brand-new session (see isSessionDeath).
        if (!isSessionDeath(err)) throw err;
      }
    }
    const authKey = await store.loadAuthKey().catch(() => undefined);
    const bundle = await this.loginWithKey(userId, identity.seed, authKey);
    this.setTokens(bundle);
    await store.saveRefreshToken(bundle.refreshToken);
  }

  async signOut(): Promise<void> {
    await this.dropAllLocalState();
  }

  /** Every trace of the current account: in-memory session, any half-built
   *  create seed, the wrapped secrets + KV, and the host's own account-scoped
   *  caches. Shared by sign-out and account deletion — there is no separate
   *  partial-wipe policy for either. */
  private async dropAllLocalState(): Promise<void> {
    this.credentials = undefined;
    this.tokenState = undefined;
    this.refreshInflight = undefined;
    this.loginInflight = undefined;
    this.refreshInDoubt = undefined; // holds a refresh token — never outlives the session
    // A prepared-but-uncommitted seed is still a secret; it must not outlive the
    // account that was being created.
    this.pendingCreate = undefined;
    this.primedRestore = undefined;
    this.primedRegister = undefined;
    const store = await this.openStore();
    await store.wipe();
    await this.deps.clearLocalData?.();
  }

  /** Reveal the 24-word phrase for the current identity (UI display only). */
  async revealPhrase(): Promise<string[]> {
    const identity = this.credentials?.identity;
    if (!identity) throw new Error('no identity');
    return seedToPhrase(identity.seed).split(' ');
  }

  // --- settings: set-password + add-passkey (iW6-D) --------------------------

  /**
   * Set or change the account password (docs/03 §3a) — the brain-side mirror of
   * screens/api/credentials.ts `setAccountPassword`, re-implemented here so the
   * WASM-free brain never imports identity/index.js (which drags
   * webauthn.ts/Capacitor). Fresh salt, split-KDF (authKey branch → `authHash`,
   * encKey branch encrypts the identity seed), guarded by a fresh identity-key
   * signature over the regular auth message. Persists the derived authKey so a
   * later silent relogin attaches the 2FA proof without re-prompting (mirrors the
   * web's `onAuthKey` → `AccountService.setStoredAuthKey`). Never logs the
   * password, keys, or seed.
   */
  async setPassword(rest: RestClient, password: string, terminateOthers: boolean): Promise<void> {
    const creds = this.credentials;
    if (!creds) throw new Error('no identity');
    const { userId, identity } = creds;
    const challenge = await rest.authChallenge(userId);
    const nonce = fromBase64(challenge.nonce);
    const signature = toBase64(await signWithIdentity(identity.seed, buildAuthMessage(nonce, userId)));
    const kdfSalt = generateKdfSalt();
    const { authKey, encKey } = await deriveSplitKeys(password, kdfSalt, this.deps.argon2);
    await rest.setPassword({
      signature,
      authHash: toBase64(authKey),
      kdfSalt: toBase64(kdfSalt),
      encIdentityKey: toBase64(encryptIdentitySeed(encKey, identity.seed)),
      ...(terminateOthers ? { terminateOthers: true } : {}),
    });
    const store = await this.openStore();
    await store.saveAuthKey(authKey);
  }

  /**
   * Remove the account password (docs/02 §3, docs/03 §3a) — the brain-side mirror
   * of screens/api/credentials.ts `removeAccountPassword`, re-implemented here so
   * the WASM-free brain never imports identity/index.js (which drags
   * webauthn.ts/Capacitor). Atomic all-null replace of the three password fields
   * (`PUT /me/password` with authHash/kdfSalt/encIdentityKey = null), guarded by
   * the same fresh identity-key signature over the regular auth message as
   * set-password (client.ts:118 fresh-key-signature guard). Clears the persisted
   * authKey — no password means no 2FA proof to keep (mirrors the web's
   * `onAuthKeyCleared` → `AccountService.setStoredAuthKey(undefined)`). Never logs
   * the keys or seed.
   */
  /**
   * Permanently delete the account (docs/03 §3b) — the brain-side mirror of
   * screens/api/credentials.ts `deleteAccount`. Root-credential gated: a FRESH
   * identity-key signature over `merkury-delete-v1 ‖ userId ‖ nonce`, plus the
   * password-derived `authKey` when the account has one. The typed-UUID
   * confirmation is UI-side friction, not enforced here.
   *
   * Throws `PasswordRequiredError` when the server demands the password proof
   * and none was supplied, so the caller can prompt and retry — the same shape
   * the login path uses. Local state is wiped by the caller (main.ts tears the
   * engine down exactly as sign-out does); this method owns only the ceremony.
   * Never logs the password, seed, or keys.
   */
  async deleteAccount(rest: RestClient, password?: string): Promise<void> {
    const creds = this.credentials;
    if (!creds) throw new Error('no identity');
    const { userId, identity } = creds;
    const challenge = await rest.authChallenge(userId);
    const nonce = fromBase64(challenge.nonce);
    const signature = toBase64(
      await signWithIdentity(identity.seed, buildDeleteMessage(userId, nonce)),
    );
    let body: DeleteMeRequest = { signature };
    if (password !== undefined && password !== '') {
      const { authKey } = await deriveSplitKeys(
        password,
        fromBase64(challenge.kdfSalt),
        this.deps.argon2,
      );
      body = { ...body, authKey: toBase64(authKey) };
    }
    try {
      await rest.deleteMe(body);
    } catch (err) {
      // 401 with no password supplied = the account has one and it is required.
      if (
        err instanceof RestError &&
        (err.status === 401 || err.status === 403) &&
        (password === undefined || password === '')
      ) {
        throw new PasswordRequiredError(userId);
      }
      throw err;
    }
    // The account is gone server-side; drop every local secret with it.
    await this.dropAllLocalState();
  }

  async removePassword(rest: RestClient, terminateOthers: boolean): Promise<void> {
    const creds = this.credentials;
    if (!creds) throw new Error('no identity');
    const { userId, identity } = creds;
    const challenge = await rest.authChallenge(userId);
    const nonce = fromBase64(challenge.nonce);
    const signature = toBase64(await signWithIdentity(identity.seed, buildAuthMessage(nonce, userId)));
    await rest.setPassword({
      signature,
      authHash: null,
      kdfSalt: null,
      encIdentityKey: null,
      ...(terminateOthers ? { terminateOthers: true } : {}),
    });
    const store = await this.openStore();
    await store.deleteAuthKey();
  }

  /**
   * Warm the passkey REGISTRATION options ahead of the tap, for the same
   * transient-activation reason as `primePasskeyRestore`. Best-effort; a miss
   * just makes `addPasskey` fetch inline.
   */
  async primeAddPasskey(rest: RestClient): Promise<void> {
    try {
      const options = await rest.registerPasskeyOptions();
      this.primedRegister = { options, at: this.now() };
    } catch {
      // best-effort; addPasskey will fetch inline
    }
  }

  private takePrimedRegister(): PasskeyRegisterOptionsResponse | undefined {
    const primed = this.primedRegister;
    this.primedRegister = undefined;
    if (!primed) return undefined;
    return this.now() - primed.at < PRIMED_OPTIONS_TTL_MS ? primed.options : undefined;
  }

  /**
   * Register a new passkey for THIS account (docs/03 §2). Runs the create
   * ceremony through the injected provider requesting the PRF extension over the
   * FIXED app-wide restore salt, wraps the identity seed under the PRF-derived
   * key when the authenticator supports PRF, and POSTs the credential (+
   * ciphertext, if any) to the server.
   *
   * `restorable` is false when the authenticator has no PRF: the passkey signs
   * this device in but cannot restore the identity on a new one, and the UI says
   * so. Resolves `null` when the ceremony produced no credential at all — that
   * is not an error (docs/09 §8 ruling); a user DISMISSAL rejects with
   * `.code === 'cancelled'`. Never logs the seed or PRF output.
   */
  async addPasskey(
    rest: RestClient,
    label?: string,
  ): Promise<{ passkey: PasskeySummary; restorable: boolean } | null> {
    const creds = this.credentials;
    if (!creds) throw new Error('no identity');
    // Prefetched on the settings screen's mount; inline only as a fallback.
    const options = this.takePrimedRegister() ?? (await rest.registerPasskeyOptions());
    const wrapped = await this.wrapSeedForPasskey(options, creds.identity.seed);
    if (!wrapped) return null;
    const trimmed = label?.trim();
    const { passkey } = await rest.registerPasskey({
      credential: wrapped.credential,
      ...(trimmed ? { label: trimmed } : {}),
      ...(wrapped.encIdentityKey ? { encIdentityKey: toBase64(wrapped.encIdentityKey) } : {}),
    });
    return { passkey, restorable: wrapped.encIdentityKey !== undefined };
  }

  /**
   * The registration wrap ceremony (docs/03 §2): create the credential
   * requesting PRF over the FIXED app-wide restore salt, harvest the PRF output
   * (via a follow-up LOCAL assertion when the create step didn't surface it —
   * create-time PRF results are unreliable across authenticators, so this
   * fallback is what actually makes passkeys restore-capable in the field), HKDF
   * it into the wrap key, and encrypt the seed.
   *
   * Returns the credential plus `encIdentityKey` when the passkey is
   * restore-capable; the credential alone (session-only) when PRF is
   * unavailable — never failing the whole registration over it. `undefined` when
   * the create ceremony yielded no credential.
   */
  private async wrapSeedForPasskey(
    options: PasskeyRegisterOptionsResponse,
    seed: Uint8Array,
  ): Promise<{ credential: WebAuthnJson; encIdentityKey?: Uint8Array } | undefined> {
    const prf = { first: PRF_RESTORE_SALT };
    const created = await this.deps.passkeys.create({ options: options.publicKey, prf });
    if (!created) return undefined;

    let prfOutput = created.prfOutput;
    if (!prfOutput && created.prfEnabled) {
      const cred = created.credential as { rawId?: string; id?: string };
      const credId = cred.rawId ?? cred.id ?? '';
      if (credId) {
        // Carry the RP ID over from the server's options: the native ceremonies
        // have no origin to infer it from and reject an assertion without it —
        // omitting it made the harvest fail on every phone, so every passkey
        // registered session-only and one-tap restore never armed.
        const rpId = (unwrapPublicKey(options.publicKey) as { rp?: { id?: string } }).rp?.id;
        prfOutput = await this.deps.passkeys
          .assert({ options: localAssertionOptions(credId, rpId), prf })
          .then((r) => r?.prfOutput)
          .catch(() => undefined);
      }
    }
    if (!prfOutput) return { credential: created.credential };
    return {
      credential: created.credential,
      encIdentityKey: encryptIdentitySeed(derivePasskeyWrapKey(prfOutput), seed),
    };
  }

  // --- HTTP: the unauthenticated auth endpoints (over the injected fetch) ----

  private async register(publicKeyB64: string, displayName: string): Promise<{ userId: string }> {
    const res = await this.postUnauth('/register', { publicKey: publicKeyB64, displayName });
    return RegisterResponse.parse(res);
  }

  private async loginWithKey(
    userId: string,
    seed: Uint8Array,
    authKey?: Uint8Array,
  ): Promise<TokenBundle> {
    const challenge = AuthChallengeResponse.parse(await this.postUnauth('/auth/challenge', { userId }));
    const nonce = fromBase64(challenge.nonce);
    const signature = await signWithIdentity(seed, buildAuthMessage(nonce, userId));
    try {
      return AuthVerifyResponse.parse(
        await this.postUnauth('/auth/verify', {
          userId,
          nonce: challenge.nonce,
          signature: toBase64(signature),
          kind: this.deps.sessionKind ?? 'web',
          ...(authKey ? { authKey: toBase64(authKey) } : {}),
        }),
      );
    } catch (err) {
      if (err instanceof AuthHttpError && err.code === 'password_required') {
        throw new PasswordRequiredError(userId);
      }
      throw err;
    }
  }

  private async deriveAuthKey(userId: string, password: string): Promise<Uint8Array> {
    const challenge = AuthChallengeResponse.parse(await this.postUnauth('/auth/challenge', { userId }));
    const { authKey } = await deriveSplitKeys(password, fromBase64(challenge.kdfSalt), this.deps.argon2);
    return authKey;
  }

  private async loginWithPassword(
    userId: string,
    password: string,
  ): Promise<{ tokens: TokenBundle; seed: Uint8Array }> {
    const challenge = AuthChallengeResponse.parse(await this.postUnauth('/auth/challenge', { userId }));
    const { authKey, encKey } = await deriveSplitKeys(
      password,
      fromBase64(challenge.kdfSalt),
      this.deps.argon2,
    );
    const response = PasswordLoginResponse.parse(
      await this.postUnauth('/auth/password', {
        userId,
        authKey: toBase64(authKey),
        kind: this.deps.sessionKind ?? 'web',
      }),
    );
    const seed = decryptIdentitySeed(encKey, fromBase64(response.encIdentityKey));
    const { encIdentityKey: _drop, ...tokens } = response;
    return { tokens, seed };
  }

  /**
   * The ONE place a refresh token is presented — every caller (single-flight
   * refresh, recovery, the login resume leg) comes through here, which is what
   * makes the no-replay guarantee structural rather than a special case at each
   * call site.
   */
  private async refresh(refreshToken: string): Promise<TokenBundle> {
    const doubt = this.refreshInDoubt;
    if (doubt !== undefined && doubt.token === refreshToken) throw doubt.err;
    try {
      return RefreshResponse.parse(await this.postUnauth('/auth/refresh', { refreshToken }));
    } catch (err) {
      // Only a 401/403 is a verdict on the token. Anything else — transport,
      // 5xx, an unreadable body — leaves us unable to say whether the server
      // spent it, so it must not be presented again inside this attempt.
      if (authRejection(err) === undefined) this.refreshInDoubt = { token: refreshToken, err };
      throw err;
    }
  }

  private async postUnauth(path: string, body: unknown): Promise<unknown> {
    // Resolved per call, never cached: the origin moves when the signaling race
    // names a new winner or the current one stops answering.
    const at = this.origin.base();
    const response = await this.fetchFn(`${at}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let code: string | undefined;
      let message = `request to ${path} failed with status ${response.status}`;
      try {
        const parsed = (await response.json()) as { code?: string; message?: string };
        if (parsed && typeof parsed.code === 'string') code = parsed.code;
        if (parsed && typeof parsed.message === 'string') message = parsed.message;
      } catch {
        // non-JSON error body — keep the generic message
      }
      // A coded body is our server's signature (api/client.ts noteResponse):
      // an uncoded error is an interceptor or a host that does not serve the
      // API, and on an unproven origin that moves the NEXT call elsewhere.
      this.origin.noteResponse(at, code !== undefined);
      throw new AuthHttpError(response.status, code, message);
    }
    return response.json();
  }
}

// --- passkey glue that is PLATFORM-INDEPENDENT (the encodings live in the
//     providers; identity/passkeyProvider.ts) ---------------------------------

/**
 * Minimal assertion options targeting the just-created credential, to harvest
 * the PRF output locally right after registration (never sent to the server —
 * the challenge is irrelevant, nothing verifies it).
 *
 * `rpId` is NOT optional in practice even though a browser can infer it from the
 * origin: on the native seams these options go verbatim into Credential Manager
 * / ASAuthorization, which have no origin to infer from and reject the request
 * outright. Wrapped in `{publicKey:…}` so every provider's `unwrapPublicKey`
 * handles it identically to a server-issued options object.
 */
function localAssertionOptions(credentialIdB64url: string, rpId?: string): WebAuthnJson {
  return {
    publicKey: {
      challenge: bytesToB64url(randomBytes(32)),
      allowCredentials: [{ id: credentialIdB64url, type: 'public-key' }],
      userVerification: 'required',
      timeout: 60_000,
      ...(rpId ? { rpId } : {}),
    },
  };
}

/** A shallow clone with the PRF outputs dropped from the client-extension results
 *  (they must never reach the server — docs/03 §2). */
function withoutPrfResults(cred: Record<string, unknown>): Record<string, unknown> {
  const ext = cred.clientExtensionResults as Record<string, unknown> | undefined;
  if (!ext || !('prf' in ext)) return cred;
  const { prf: _prf, ...rest } = ext;
  return { ...cred, clientExtensionResults: rest };
}

/** Decode the assertion's `userHandle` (base64url) to the account UUID string —
 *  the server sets `WebAuthnID()` to the lowercase UUID (backend passkeys.go). */
function userHandleUserId(cred: Record<string, unknown>): string | undefined {
  const raw = (cred.response as { userHandle?: string | null } | undefined)?.userHandle;
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  // The handle is an ASCII UUID; decode without TextDecoder (JSC-portable).
  let s = '';
  for (const b of b64urlToBytes(raw)) s += String.fromCharCode(b);
  return s.toLowerCase();
}

/** Try the fixed-salt wrap key first, then the legacy per-user-salt key —
 *  Poly1305 makes a wrong-key attempt fail loudly, never yield garbage. Mirrors
 *  auth.ts `unwrapSeed`. */
function unwrapPasskeySeed(
  blob: Uint8Array,
  first: Uint8Array,
  second?: Uint8Array,
): Uint8Array | undefined {
  for (const output of [first, second]) {
    if (!output) continue;
    try {
      return decryptIdentitySeed(derivePasskeyWrapKey(output), blob);
    } catch {
      // Wrong key branch for this blob — try the next.
    }
  }
  return undefined;
}
